import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Languages } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from '@/i18n';

const NEXT: Record<SupportedLanguage, SupportedLanguage> = {
  ro: 'en',
  en: 'ro',
};

function normalize(lng: string | undefined): SupportedLanguage {
  const base = (lng ?? 'ro').toLowerCase().split('-')[0] as string;
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(base)
    ? (base as SupportedLanguage)
    : 'ro';
}

export function LanguageToggle(): ReactElement {
  const { t, i18n } = useTranslation();
  const current = normalize(i18n.resolvedLanguage ?? i18n.language);
  const next = NEXT[current];

  const currentLabel = t(`lang.${current}`);
  const nextLabel = t(`lang.${next}`);
  const title = t('lang.toggleTitle', { current: currentLabel, next: nextLabel });

  const onClick = (): void => {
    void i18n.changeLanguage(next);
  };

  return (
    <Button
      variant="ghost"
      onClick={onClick}
      title={title}
      aria-label={title}
      className="h-9 gap-1.5 px-2"
      data-testid="language-toggle"
    >
      <Languages className="h-4 w-4" aria-hidden="true" focusable="false" />
      <span className="text-xs font-semibold uppercase">{t(`lang.short.${current}`)}</span>
    </Button>
  );
}
